import NCAAFService from "../../../../components/dashboards/services/ncaaService";
import { NCAAF_LEAGUES } from "../constants/leagues";
import { USA } from "../constants/countries";

const LIVE_STATUS_CODES = ["Q1", "Q2", "Q3", "Q4", "HT", "OT", "LIVE", "IN PLAY"];
const FINISHED_STATUS_CODES = ["FT", "AOT", "AET", "PEN"];
const DEFAULT_TIMEZONE = "America/New_York";

const getCurrentSeason = () =>
  typeof NCAAFService.getCurrentSeason === "function"
    ? NCAAFService.getCurrentSeason()
    : new Date().getFullYear();

const getTodayDate = () =>
  typeof NCAAFService.getDefaultGameDate === "function"
    ? NCAAFService.getDefaultGameDate()
    : typeof NCAAFService.getTodayDate === "function"
      ? NCAAFService.getTodayDate()
      : new Date().toISOString().split("T")[0];

const getNextSaturday = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  // Calculate days until next Saturday (6 = Saturday)
  // If today is Saturday (6), use today (0 days), otherwise calculate days until next Saturday
  const daysUntilSaturday = dayOfWeek === 6 ? 0 : dayOfWeek === 0 ? 6 : 6 - dayOfWeek;
  const targetDate = new Date(today);
  targetDate.setDate(today.getDate() + daysUntilSaturday);
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, "0");
  const day = String(targetDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseSeason = (season) => {
  if (!season) return getCurrentSeason();
  const numeric = parseInt(season, 10);
  return Number.isNaN(numeric) ? getCurrentSeason() : numeric;
};

export const getDefaultFilters = () => ({
  date: getNextSaturday(), // Default to next Saturday (current week) for NCAAF
  league: String(NCAAF_LEAGUES[0]?.id || 2),
  season: String(getCurrentSeason()),
  country: "",
  conference: "", // Empty string shows "All Conferences" in the UI
  division: "all",
  teamId: null,
  status: "all"
});

export const getLeagues = () => NCAAF_LEAGUES;

export const getSeasons = () =>
  typeof NCAAFService.getAvailableSeasons === "function"
    ? NCAAFService.getAvailableSeasons()
    : [getCurrentSeason()];

export const getConferences = async (league, season) => {
  try {
    const response = await NCAAFService.getConferences(
      Number(league) || 2,
      parseSeason(season)
    );
    return response?.response || [];
  } catch (error) {
    console.error("Failed to load NCAAF conferences:", error);
    return [];
  }
};

export const getDivisions = async (league, season) => {
  try {
    const response = await NCAAFService.getDivisions(
      Number(league) || 2,
      parseSeason(season)
    );
    return response?.response || [];
  } catch (error) {
    console.error("Failed to load NCAAF divisions:", error);
    return [];
  }
};

const sortGamesByStatus = (games = []) => {
  return [...games].sort((a, b) => {
    const aStatus = a?.game?.status?.short;
    const bStatus = b?.game?.status?.short;
    const aLive = LIVE_STATUS_CODES.includes(aStatus);
    const bLive = LIVE_STATUS_CODES.includes(bStatus);

    if (aLive && !bLive) return -1;
    if (!aLive && bLive) return 1;

    const aFinished = FINISHED_STATUS_CODES.includes(aStatus);
    const bFinished = FINISHED_STATUS_CODES.includes(bStatus);

    if (aLive && bLive) {
      const statusOrder = {
        Q1: 1,
        Q2: 2,
        HT: 3,
        Q3: 4,
        Q4: 5,
        OT: 6,
        "IN PLAY": 0,
        LIVE: 0
      };
      return (statusOrder[aStatus] || 7) - (statusOrder[bStatus] || 7);
    }

    if (aFinished && !bFinished) return 1;
    if (!aFinished && bFinished) return -1;

    const aDate = a?.game?.date
      ? new Date(a.game.date).getTime()
      : a?.game?.date?.timestamp || 0;
    const bDate = b?.game?.date
      ? new Date(b.game.date).getTime()
      : b?.game?.date?.timestamp || 0;

    return aDate - bDate;
  });
};

const filterGamesByStatus = (games = [], status) => {
  if (status === "live") {
    return games.filter((game) => LIVE_STATUS_CODES.includes(game?.game?.status?.short));
  }
  if (status === "upcoming") {
    return games.filter((game) => game?.game?.status?.short === "NS");
  }
  return games;
};

const teamHasConference = (team, conference) =>
  (team?.conference || "")
    .toString()
    .toLowerCase()
    .includes(conference.toLowerCase());

export const getGames = async ({
  date,
  league,
  season,
  country,
  teamId,
  status = "all",
  conference,
  division,
  standings = [],
  teams = []
}) => {
  const normalizedSeason = parseSeason(season);
  const normalizedLeague = Number(league) || 2;
  const normalizedTeam = teamId ? Number(teamId) : null;
  const finalDate = date || getTodayDate();

  const response = await NCAAFService.getGames(
    null,
    finalDate,
    normalizedLeague,
    normalizedSeason,
    normalizedTeam,
    null,
    status === "live" ? "all" : null,
    DEFAULT_TIMEZONE
  );

  let games = response?.response || [];

  if (country) {
    const normalizedCountry = country.toLowerCase();
    games = games.filter((game) => {
      const leagueCountry = game?.league?.country?.toLowerCase();
      const flagCountry = game?.league?.flag?.toLowerCase();
      return (
        leagueCountry === normalizedCountry ||
        flagCountry === normalizedCountry ||
        normalizedCountry === USA.code.toLowerCase()
      );
    });
  }

  if (conference) {
    // Handle both string and object inputs for conference
    const conferenceStr = typeof conference === 'string'
      ? conference
      : conference?.name || conference?.id || String(conference);

    const normalizedConference = conferenceStr.toLowerCase();

    games = games.filter((game) => {
      const homeConf = game?.teams?.home?.conference;
      const awayConf = game?.teams?.away?.conference;

      if (homeConf || awayConf) {
        return (
          teamHasConference({ conference: homeConf }, normalizedConference) ||
          teamHasConference({ conference: awayConf }, normalizedConference)
        );
      }

      if (teams.length > 0) {
        const homeTeam = teams.find((t) => t.id === game?.teams?.home?.id);
        const awayTeam = teams.find((t) => t.id === game?.teams?.away?.id);
        return (
          (homeTeam && teamHasConference(homeTeam, normalizedConference)) ||
          (awayTeam && teamHasConference(awayTeam, normalizedConference))
        );
      }

      return true;
    });
  }

  if (division === "top25") {
    let standingsData = standings;
    if (!standingsData || standingsData.length === 0) {
      standingsData = await getStandings({
        league: normalizedLeague,
        season: normalizedSeason
      });
    }
    const top25Ids = (standingsData || [])
      .slice(0, 25)
      .map((standing) => standing?.team?.id)
      .filter(Boolean);

    games = games.filter((game) => {
      const homeId = game?.teams?.home?.id;
      const awayId = game?.teams?.away?.id;
      return top25Ids.includes(homeId) || top25Ids.includes(awayId);
    });
  }

  const filteredByStatus = filterGamesByStatus(games, status);
  return sortGamesByStatus(filteredByStatus);
};

export const getStandings = async ({ league, season, conference, division }) => {
  const response = await NCAAFService.getStandings(
    Number(league) || 2,
    parseSeason(season),
    null,
    conference || null,
    division || null
  );

  return response?.response || [];
};

export const getTeams = async ({ league, season, conference }) => {
  const normalizedLeague = Number(league) || 2;
  const normalizedSeason = parseSeason(season);
  let teams = [];

  try {
    const standings = await getStandings({
      league: normalizedLeague,
      season: normalizedSeason,
      conference: "",
      division: ""
    });

    teams =
      standings
        .map((standing) => ({
          id: standing?.team?.id,
          name: standing?.team?.name,
          logo: standing?.team?.logo,
          code: standing?.team?.code,
          conference: standing?.conference || standing?.group,
          division: standing?.division,
          city: standing?.team?.city,
          stadium: standing?.team?.venue?.name
        }))
        .filter((team) => team.id) || [];
  } catch (error) {
    console.warn("Failed to derive teams from standings, falling back:", error);
  }

  if (teams.length === 0) {
    try {
      const response = await NCAAFService.getTeams(
        null,
        normalizedLeague,
        normalizedSeason
      );
      teams =
        response?.response?.map((team) => ({
          id: team?.id,
          name: team?.name,
          logo: team?.logo,
          code: team?.code,
          conference: team?.conference || team?.group,
          division: team?.division,
          city: team?.city || team?.name,
          stadium: team?.stadium
        })) || [];
    } catch (error) {
      console.error("Failed to load NCAAF teams:", error);
      teams = [];
    }
  }

  if (conference) {
    const normalizedConference = conference.toLowerCase();
    teams = teams.filter((team) => teamHasConference(team, normalizedConference));
  }

  return teams;
};

export default {
  getDefaultFilters,
  getLeagues,
  getSeasons,
  getGames,
  getStandings,
  getTeams,
  getConferences,
  getDivisions
};
